import { useState, useEffect } from 'react';
import { useEditMode } from '@/contexts/EditModeContext';
import { useAuth } from '@/contexts/AuthContext';
import { Upload, Check, X, Pencil, Crop } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { ImageCropModal } from './ImageCropModal';
import { toast } from 'sonner';

interface EditableImageProps {
  src: string;
  alt: string;
  onSave: (url: string) => Promise<void>;
  className?: string;
  containerClassName?: string;
}

export const EditableImage = ({
  src,
  alt,
  onSave,
  className,
  containerClassName,
}: EditableImageProps) => {
  const { isEditMode } = useEditMode();
  const { isEditor } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [imageUrl, setImageUrl] = useState(src);
  const [isSaving, setIsSaving] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  // Sync local state when src prop changes
  useEffect(() => {
    setImageUrl(src);
  }, [src]);

  if (!isEditor || !isEditMode) {
    return <img src={src} alt={alt} className={className} />;
  }

  const handleSave = async () => {
    if (!imageUrl.trim()) {
      alert('Please enter a valid image URL');
      return;
    }

    setIsSaving(true);
    try {
      await onSave(imageUrl);
      setIsEditing(false);
    } catch (error) {
      console.error('Error saving:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setImageUrl(src);
    setIsEditing(false);
    setSelectedFile(null);
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    setSelectedFile(file);
    const reader = new FileReader();
    reader.onloadend = () => {
      setImageToCrop(reader.result as string);
      setShowCropModal(true);
    };
    reader.readAsDataURL(file);
  };

  const handleCropSave = async (croppedBlob: Blob) => {
    setIsSaving(true);
    try {
      // Convert blob to data URL for preview
      const reader = new FileReader();
      reader.onloadend = async () => {
        const dataUrl = reader.result as string;
        setImageUrl(dataUrl);
        await onSave(dataUrl);
        setIsEditing(false);
        setShowCropModal(false);
        toast.success('Image cropped and saved successfully');
      };
      reader.readAsDataURL(croppedBlob);
    } catch (error) {
      console.error('Error saving cropped image:', error);
      toast.error('Failed to save cropped image');
    } finally {
      setIsSaving(false);
    }
  };

  if (isEditing) {
    return (
      <>
        <div className={cn('space-y-4', containerClassName)}>
          <div className="space-y-2">
            <Label>Upload Image</Label>
            <div className="flex gap-2">
              <Input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="flex-1"
              />
              <Button
                variant="outline"
                size="icon"
                title="Crop uploaded image"
                disabled={!selectedFile}
                onClick={() => {
                  if (selectedFile && imageToCrop) {
                    setShowCropModal(true);
                  }
                }}
              >
                <Crop className="h-4 w-4" />
              </Button>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label>Or paste Image URL</Label>
            <Input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div className="bg-muted rounded-lg p-4 flex items-center justify-center min-h-[200px]">
            {imageUrl ? (
              <img
                src={imageUrl}
                alt="Preview"
                className={cn('max-w-full max-h-[300px] object-contain', className)}
                onError={(e) => {
                  e.currentTarget.src = 'https://cdn3.iconfinder.com/data/icons/communication-social-media-1/24/account_profile_user_contact_person_avatar_placeholder-512.png';
                }}
              />
            ) : (
              <p className="text-muted-foreground">Upload or enter URL to preview</p>
            )}
          </div>
          <div className="flex gap-2">
            <Button onClick={handleSave} disabled={isSaving}>
              <Check className="h-4 w-4 mr-1" />
              Save
            </Button>
            <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
              <X className="h-4 w-4 mr-1" />
              Cancel
            </Button>
          </div>
        </div>

        {imageToCrop && (
          <ImageCropModal
            isOpen={showCropModal}
            onClose={() => setShowCropModal(false)}
            imageUrl={imageToCrop}
            onSave={handleCropSave}
            aspectRatio={1}
          />
        )}
      </>
    );
  }

  return (
    <div className={cn('group relative', containerClassName)}>
      <img
        src={src}
        alt={alt}
        className={cn(
          'border-2 border-dashed border-transparent hover:border-orange-300 transition-all cursor-pointer rounded',
          className
        )}
        onClick={() => setIsEditing(true)}
      />
      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
        <Button
          size="lg"
          className="bg-orange-500 hover:bg-orange-600"
          onClick={() => setIsEditing(true)}
        >
          <Pencil className="h-4 w-4 mr-2" />
          Change Image
        </Button>
      </div>
    </div>
  );
};
